import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { Stack, Alert } from "@mui/material";
import { AddCircle } from "@mui/icons-material";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));

const FlowBuilderAddButtonsModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [buttons, setButtons] = useState([]);
  const [buttonText, setButtonText] = useState("");

  const [labels, setLabels] = useState({
    title: "Adicionar botões ao fluxo",
    btn: "Adicionar"
  });

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar botões",
        btn: "Salvar"
      });
      setMessage(data.data.message || "");
      setButtons(data.data.buttons || []);
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: "Adicionar botões ao fluxo",
        btn: "Adicionar"
      });
      setMessage("");
      setButtons([]);
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
    setMessage("");
    setButtons([]);
    setButtonText("");
  };

  const handleAddButton = () => {
    if (!buttonText.trim()) {
      toast.error("Digite o texto do botão");
      return;
    }

    if (buttons.length >= 3) {
      toast.error("Máximo de 3 botões permitidos");
      return;
    }

    const newButton = {
      id: `${buttons.length + 1}`,
      displayText: buttonText
    };

    setButtons([...buttons, newButton]);
    setButtonText("");
  };

  const handleRemoveButton = (index) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!message.trim()) {
      toast.error("Digite a mensagem dos botões");
      return;
    }

    if (buttons.length === 0) {
      toast.error("Adicione pelo menos um botão");
      return;
    }

    if (open === "edit") {
      handleClose();
      onUpdate({
        ...data,
        data: { message, buttons }
      });
    } else {
      handleClose();
      onSave({
        message,
        buttons
      });
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth="md"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Stack>
          <DialogContent dividers>
            <Stack gap={"16px"}>
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Informação
                </Typography>
                <Typography variant="caption">
                  Crie botões interativos para o usuário selecionar. Máximo de 3 botões.
                </Typography>
              </Alert>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Mensagem dos botões"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                variant="outlined"
                placeholder="Digite a mensagem que será exibida com os botões"
              />

              <div>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Botões ({buttons.length}/3)
                </Typography>

                <Stack gap={1} sx={{ mb: 2 }}>
                  {buttons.map((button, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        backgroundColor: "#f3f4f6",
                        padding: "12px",
                        borderRadius: "8px"
                      }}
                    >
                      <Typography variant="body2">
                        {index + 1}. {button.displayText}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveButton(index)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>

                {buttons.length < 3 && (
                  <Stack direction="row" gap={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Texto do botão"
                      value={buttonText}
                      onChange={(e) => setButtonText(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleAddButton();
                        }
                      }}
                      variant="outlined"
                      placeholder="Digite o texto do botão"
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddButton}
                      sx={{ minWidth: "120px" }}
                    >
                      <AddCircle sx={{ mr: 1 }} />
                      Adicionar
                    </Button>
                  </Stack>
                )}
              </div>
            </Stack>
          </DialogContent>

          <DialogActions>
            {!loading && (
              <>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  variant="outlined"
                >
                  {i18n.t("contactModal.buttons.cancel")}
                </Button>
                <Button
                  disabled={loading}
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                  onClick={handleSave}
                >
                  {`${labels.btn}`}
                </Button>
              </>
            )}
          </DialogActions>
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddButtonsModal;
